/**
 * DropPage's entry-params capture — the one-time URL param reads (?hubRef
 * referral, post-claim landing, ?tenant override) and the OAuth continuation
 * URLs they derive. Extracted verbatim from DropPage against the 400-line
 * ceiling; each block's rationale comment travels with it.
 */
import {useMemo, useState} from "react";
import getConfig from "@/utils/getConfig";
import type {TenantId} from "@/widgets/ThemeProvider";

export function useDropPageEntry(dropSocials: Array<{ platform?: string }> | undefined) {
    // Capture ?hubRef= referral param from URL on initial load (one-time)
    const referralUserId = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get("hubRef");
        return ref && (/^[a-f\d]{24}$/i).test(ref) ? ref : undefined;
    }, []);

    /*
     * Post-claim landing (one-time capture). The web Add/Claim OAuth callback
     * redirects here with ?created=1|claimed=1&via=twitch|kick — WITHOUT a web
     * session (only the portal flow mints one). Without this banner the
     * creator lands on their own Drop with zero acknowledgment and no
     * authenticated next step. `via` powers the one-click portal continuation:
     * they just OAuthed with that platform, so the portal login completes
     * without re-prompting.
     */
    const postClaim = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const created = params.get("created") === "1";
        const claimed = params.get("claimed") === "1";
        if (!created && !claimed) return undefined;
        const viaRaw = params.get("via");
        /*
         * Strip the one-time claim params immediately (other params like
         * ?tenant= survive) — otherwise a refresh replays the banner after
         * dismiss, and a bookmarked/shared URL shows "Your Drop is live!" to
         * visitors who didn't claim anything.
         */
        params.delete("created");
        params.delete("claimed");
        params.delete("via");
        const remaining = params.toString();
        window.history.replaceState(null, "", window.location.pathname + (remaining ? `?${remaining}` : ""));
        return {
            created,
            via: viaRaw === "twitch" || viaRaw === "kick" ? viaRaw : undefined,
        };
    }, []);
    const [postClaimDismissed, setPostClaimDismissed] = useState(false);
    const portalLoginUrl = useMemo(() => {
        if (!postClaim?.via) return undefined;
        const apiBase = (getConfig().basePath ?? "").replace(/\/$/, "");
        return (/^https?:\/\//i).test(apiBase)
            ? `${apiBase}/drop-in/oauth/${postClaim.via}/login?origin=portal`
            : undefined;
    }, [postClaim]);

    /*
     * Tenant override via `?tenant=` query param. Drives ThemeProvider's
     * per-tenant base layer (e.g. Kick brand colors when hosted on Kick).
     * Defaults to native DevourPlay rendering when unset. Once DropModel
     * carries `tenantId` (Tenant Isolation Phase 1), this resolves from
     * the Drop record instead of the URL.
     */
    const tenantId = useMemo<TenantId | undefined>(() => {
        const t = new URLSearchParams(window.location.search).get("tenant");
        return t === "kick" || t === "devourplay" ? t : undefined;
    }, []);

    /*
     * Cross-platform prompt: after a claim, offer to connect the OTHER platform
     * (Twitch↔Kick) when the Drop doesn't already carry it — so a creator who
     * claimed via Kick can attach their Twitch (and vice-versa) onto the same
     * Drop instead of ending up with a duplicate.
     */
    const otherPlatform = postClaim?.via === "kick" ? "twitch" : postClaim?.via === "twitch" ? "kick" : undefined;
    const addOtherPlatformUrl = (() => {
        if (!otherPlatform) return undefined;
        const hasOther = (dropSocials ?? []).some(s => (s.platform ?? "").toLowerCase() === otherPlatform);
        if (hasOther) return undefined;
        const apiBase = (getConfig().basePath ?? "").replace(/\/$/, "");
        return (/^https?:\/\//i).test(apiBase) ? `${apiBase}/drop-in/oauth/${otherPlatform}/login` : undefined;
    })();
    return {referralUserId, postClaim, postClaimDismissed, setPostClaimDismissed, portalLoginUrl, tenantId, otherPlatform, addOtherPlatformUrl};
}
